'use client';
import React, { useState } from 'react';
import AppLayout from '@/components/AppLayout';
import { Package, AlertTriangle, Ship, TrendingUp, Shield, Search, MapPin } from 'lucide-react';
import { BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, Cell } from 'recharts';

const SHIPMENTS = [
  { id: 'CBP-2025-004821', vessel: 'MV Atlantic Star', origin: 'China', port: 'Freeport of Monrovia', declared: '$2,840,000', riskScore: 87, flag: 'Under-invoicing', status: 'flagged', date: '2025-05-27', cargo: 'Electronics' },
  { id: 'CBP-2025-004790', vessel: 'MV Liberia Pride', origin: 'Netherlands', port: 'Buchanan Port', declared: '$1,120,000', riskScore: 22, flag: null, status: 'cleared', date: '2025-05-27', cargo: 'Machinery' },
  { id: 'CBP-2025-004763', vessel: 'MV Gulf Trader', origin: 'Nigeria', port: 'Freeport of Monrovia', declared: '$480,000', riskScore: 74, flag: 'Suspicious declaration', status: 'under-review', date: '2025-05-26', cargo: 'Textiles' },
  { id: 'CBP-2025-004741', vessel: 'MV West Africa Express', origin: 'USA', port: 'Greenville Port', declared: '$3,200,000', riskScore: 15, flag: null, status: 'cleared', date: '2025-05-26', cargo: 'Food Commodities' },
  { id: 'CBP-2025-004718', vessel: 'MV Ivory Coast', origin: 'Côte d\'Ivoire', port: 'Freeport of Monrovia', declared: '$890,000', riskScore: 61, flag: 'Mismatch', status: 'under-review', date: '2025-05-25', cargo: 'Chemicals' },
  { id: 'CBP-2025-004692', vessel: 'MV Sahara Wind', origin: 'Morocco', port: 'Buchanan Port', declared: '$540,000', riskScore: 91, flag: 'Smuggling risk', status: 'flagged', date: '2025-05-25', cargo: 'Mixed Goods' },
  { id: 'CBP-2025-004671', vessel: 'MV Pacific Dawn', origin: 'South Korea', port: 'Freeport of Monrovia', declared: '$4,100,000', riskScore: 18, flag: null, status: 'cleared', date: '2025-05-24', cargo: 'Vehicles' },
];

const riskByOrigin = [
  { origin: 'China', score: 72 },
  { origin: 'Nigeria', score: 68 },
  { origin: 'Morocco', score: 84 },
  { origin: 'Côte d\'Ivoire', score: 55 },
  { origin: 'USA', score: 18 },
  { origin: 'Netherlands', score: 14 },
  { origin: 'South Korea', score: 21 },
];

const statusConfig: Record<string, { color: string; bg: string; label: string }> = {
  flagged: { color: '#f87171', bg: 'rgba(248,113,113,0.12)', label: 'Flagged' },
  'under-review': { color: '#fbbf24', bg: 'rgba(251,191,36,0.12)', label: 'Under Review' },
  cleared: { color: '#34d399', bg: 'rgba(52,211,153,0.12)', label: 'Cleared' },
};

const kpis = [
  { label: 'Shipments (May)', value: '1,847', icon: Ship, color: 'var(--accent)' },
  { label: 'Flagged', value: '127', icon: AlertTriangle, color: '#f87171' },
  { label: 'Customs Revenue', value: '$48.2M', icon: TrendingUp, color: '#34d399' },
  { label: 'Cleared Today', value: '94', icon: Shield, color: '#60a5fa' },
];

export default function CustomsBorderClient() {
  const [statusFilter, setStatusFilter] = useState('All');
  const [search, setSearch] = useState('');

  const filtered = SHIPMENTS.filter((s) => {
    const matchStatus = statusFilter === 'All' || s.status === statusFilter.toLowerCase().replace(' ', '-');
    const matchSearch = !search || s.vessel.toLowerCase().includes(search.toLowerCase()) || s.id.toLowerCase().includes(search.toLowerCase()) || s.origin.toLowerCase().includes(search.toLowerCase());
    return matchStatus && matchSearch;
  });

  return (
    <AppLayout>
      <div className="min-h-screen px-4 py-5 lg:px-6 xl:px-8 max-w-screen-2xl mx-auto">
        {/* Header */}
        <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3 mb-6">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-lg flex items-center justify-center flex-shrink-0"
              style={{ background: 'linear-gradient(135deg, #b8860b 0%, #d4a017 100%)' }}>
              <Package size={20} style={{ color: '#070e1f' }} />
            </div>
            <div>
              <h1 className="text-lg sm:text-xl font-bold" style={{ color: 'var(--dark-text)' }}>Customs & Border Intelligence</h1>
              <p className="text-xs sm:text-sm" style={{ color: 'var(--dark-muted)' }}>AI cargo risk detection · Port analytics · Trade intelligence · Smuggling prevention</p>
            </div>
          </div>
          <div className="flex items-center gap-2 px-3 py-1.5 rounded-full text-xs font-semibold"
            style={{ backgroundColor: 'rgba(248,113,113,0.15)', color: '#f87171', border: '1px solid rgba(248,113,113,0.3)' }}>
            <AlertTriangle size={12} />
            127 Flagged Shipments
          </div>
        </div>

        {/* KPIs */}
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 mb-5">
          {kpis.map((k) => {
            const KIcon = k.icon;
            return (
              <div key={k.label} className="rounded-xl px-4 py-4 flex items-center gap-3"
                style={{ backgroundColor: 'var(--dark-card)', border: '1px solid var(--dark-border)' }}>
                <div className="w-9 h-9 rounded-lg flex items-center justify-center flex-shrink-0" style={{ backgroundColor: `${k.color}18` }}>
                  <KIcon size={18} style={{ color: k.color }} />
                </div>
                <div>
                  <p className="text-xs" style={{ color: 'var(--dark-muted)' }}>{k.label}</p>
                  <p className="text-lg font-bold font-mono-data" style={{ color: k.color }}>{k.value}</p>
                </div>
              </div>
            );
          })}
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-4 mb-4">
          {/* Shipments table */}
          <div className="lg:col-span-2 rounded-xl overflow-hidden" style={{ backgroundColor: 'var(--dark-card)', border: '1px solid var(--dark-border)' }}>
            <div className="flex items-center gap-3 px-4 py-3 border-b flex-wrap" style={{ borderColor: 'var(--dark-border)' }}>
              <div className="relative flex-1 min-w-40">
                <Search size={13} className="absolute left-2.5 top-1/2 -translate-y-1/2" style={{ color: 'var(--dark-muted)' }} />
                <input type="text" placeholder="Search vessel, ID, origin…" value={search} onChange={(e) => setSearch(e.target.value)}
                  className="w-full pl-8 pr-3 py-1.5 rounded-md text-xs outline-none"
                  style={{ backgroundColor: 'var(--dark-elevated)', color: 'var(--dark-text)', border: '1px solid var(--dark-border)' }} />
              </div>
              {['All', 'Flagged', 'Under Review', 'Cleared'].map((s) => (
                <button key={s} onClick={() => setStatusFilter(s)}
                  className="px-2.5 py-1 rounded-full text-xs font-medium transition-all"
                  style={{
                    backgroundColor: statusFilter === s ? 'rgba(212,160,23,0.15)' : 'var(--dark-elevated)',
                    color: statusFilter === s ? 'var(--accent)' : 'var(--dark-muted)',
                    border: `1px solid ${statusFilter === s ? 'rgba(212,160,23,0.4)' : 'var(--dark-border)'}`,
                  }}>
                  {s}
                </button>
              ))}
            </div>
            <div className="overflow-x-auto">
              <table className="w-full text-xs">
                <thead>
                  <tr style={{ borderBottom: '1px solid var(--dark-border)' }}>
                    {['CBP ID', 'Vessel', 'Origin', 'Port', 'Declared Value', 'Cargo', 'Risk', 'Status'].map((h) => (
                      <th key={h} className="px-4 py-3 text-left font-semibold" style={{ color: 'var(--dark-muted)' }}>{h}</th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {filtered.map((s) => {
                    const sc = statusConfig[s.status];
                    return (
                      <tr key={s.id} className="transition-colors" style={{ borderBottom: '1px solid var(--dark-border)' }}
                        onMouseEnter={(e) => { (e.currentTarget as HTMLElement).style.backgroundColor = 'rgba(255,255,255,0.02)'; }}
                        onMouseLeave={(e) => { (e.currentTarget as HTMLElement).style.backgroundColor = 'transparent'; }}>
                        <td className="px-4 py-3 font-mono-data font-semibold" style={{ color: 'var(--accent)' }}>{s.id}</td>
                        <td className="px-4 py-3 font-medium" style={{ color: 'var(--dark-text)' }}>{s.vessel}</td>
                        <td className="px-4 py-3" style={{ color: 'var(--dark-muted)' }}>{s.origin}</td>
                        <td className="px-4 py-3 text-xs" style={{ color: 'var(--dark-muted)' }}>{s.port}</td>
                        <td className="px-4 py-3 font-mono-data" style={{ color: 'var(--dark-text)' }}>{s.declared}</td>
                        <td className="px-4 py-3" style={{ color: 'var(--dark-muted)' }}>{s.cargo}</td>
                        <td className="px-4 py-3">
                          <div className="flex items-center gap-1.5">
                            <div className="w-12 h-1.5 rounded-full overflow-hidden" style={{ backgroundColor: 'var(--dark-elevated)' }}>
                              <div className="h-full rounded-full" style={{ width: `${s.riskScore}%`, backgroundColor: s.riskScore > 70 ? '#f87171' : s.riskScore > 45 ? '#fbbf24' : '#34d399' }} />
                            </div>
                            <span className="text-xs font-semibold" style={{ color: s.riskScore > 70 ? '#f87171' : s.riskScore > 45 ? '#fbbf24' : '#34d399' }}>{s.riskScore}</span>
                          </div>
                        </td>
                        <td className="px-4 py-3">
                          <span className="px-2 py-0.5 rounded-full font-medium" style={{ backgroundColor: sc.bg, color: sc.color }}>{sc.label}</span>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </div>

          {/* Risk by origin chart */}
          <div className="rounded-xl p-4" style={{ backgroundColor: 'var(--dark-card)', border: '1px solid var(--dark-border)' }}>
            <div className="flex items-center gap-2 mb-4">
              <MapPin size={14} style={{ color: 'var(--accent)' }} />
              <p className="text-sm font-semibold" style={{ color: 'var(--dark-text)' }}>Risk Score by Origin</p>
            </div>
            <ResponsiveContainer width="100%" height={220}>
              <BarChart data={riskByOrigin} layout="vertical" margin={{ top: 0, right: 10, left: 0, bottom: 0 }}>
                <XAxis type="number" tick={{ fontSize: 10, fill: 'var(--dark-muted)' }} tickLine={false} axisLine={false} domain={[0, 100]} />
                <YAxis type="category" dataKey="origin" tick={{ fontSize: 10, fill: 'var(--dark-muted)' }} tickLine={false} axisLine={false} width={80} />
                <Tooltip contentStyle={{ backgroundColor: 'var(--dark-elevated)', border: '1px solid var(--dark-border)', borderRadius: '8px', fontSize: '11px' }} />
                <Bar dataKey="score" radius={[0, 3, 3, 0]}>
                  {riskByOrigin.map((entry) => (
                    <Cell key={entry.origin} fill={entry.score > 70 ? '#f87171' : entry.score > 45 ? '#fbbf24' : '#34d399'} />
                  ))}
                </Bar>
              </BarChart>
            </ResponsiveContainer>

            <div className="mt-4 space-y-2">
              <p className="text-xs font-semibold mb-2" style={{ color: 'var(--dark-text)' }}>Active Ports</p>
              {['Freeport of Monrovia', 'Buchanan Port', 'Greenville Port'].map((port) => (
                <div key={port} className="flex items-center gap-2 px-3 py-2 rounded-lg"
                  style={{ backgroundColor: 'var(--dark-elevated)', border: '1px solid var(--dark-border)' }}>
                  <Ship size={12} style={{ color: 'var(--accent)' }} />
                  <span className="text-xs" style={{ color: 'var(--dark-text)' }}>{port}</span>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>
    </AppLayout>
  );
}
